import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { SERVICES, buildTrackedUrl, logServiceClick } from '../data/kmsServices';
import { ArrowLeft, ExternalLink, ShieldCheck, AlertTriangle, Info, Landmark, Building2 } from 'lucide-react';
import useSeo from '../hooks/useSeo';

const ACCENT_TEXT = {
  emerald: 'text-emerald-700',
  sky:     'text-sky-700',
  amber:   'text-amber-700',
  rose:    'text-rose-700',
  green:   'text-green-700',
  indigo:  'text-indigo-700',
  violet:  'text-violet-700',
};
const ACCENT_GRAD = {
  emerald: 'from-emerald-600 to-green-600',
  sky:     'from-sky-600 to-blue-600',
  amber:   'from-amber-600 to-yellow-600',
  rose:    'from-rose-600 to-pink-600',
  green:   'from-green-600 to-emerald-600',
  indigo:  'from-indigo-600 to-blue-600',
  violet:  'from-violet-600 to-purple-600',
};
const ACCENT_BTN = {
  emerald: 'bg-emerald-600 hover:bg-emerald-700',
  sky:     'bg-sky-600 hover:bg-sky-700',
  amber:   'bg-amber-600 hover:bg-amber-700',
  rose:    'bg-rose-600 hover:bg-rose-700',
  green:   'bg-green-600 hover:bg-green-700',
  indigo:  'bg-indigo-600 hover:bg-indigo-700',
  violet:  'bg-violet-600 hover:bg-violet-700',
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const service = SERVICES[slug];
  const isRedirect = !!(service && service.redirect_to);
  const seoActive = !!(service && !isRedirect);

  useSeo({
    title: seoActive ? `${service.title} — For You | KnowMySchemes` : undefined,
    description: seoActive ? service.subtitle : undefined,
    canonical: seoActive ? `https://knowmyschemes.com/money-hub/${slug}` : undefined,
    noindex: !service ? true : undefined,
    jsonLd: seoActive ? [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.title,
        description: service.subtitle,
        url: `https://knowmyschemes.com/money-hub/${slug}`,
        provider: { '@type': 'Organization', name: 'KnowMySchemes' },
        areaServed: { '@type': 'Country', name: 'India' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://knowmyschemes.com' },
          { '@type': 'ListItem', position: 2, name: 'For You', item: 'https://knowmyschemes.com/money-hub' },
          { '@type': 'ListItem', position: 3, name: service.title, item: `https://knowmyschemes.com/money-hub/${slug}` },
        ],
      },
    ] : undefined,
  });

  // If the service is configured as a redirect (savings → existing Money Hub), do it.
  if (service && service.redirect_to) {
    return <Navigate to={service.redirect_to} replace />;
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Service not found</h1>
          <p className="mt-2 text-gray-500">The service you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/money-hub')} className="mt-6 inline-flex items-center gap-2 text-emerald-700 font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to KMS Services
          </button>
        </main>
      </div>
    );
  }

  const handlePartnerClick = (e, item) => {
    // For tracked (private partner) links we build the UTM URL and fire the log.
    // Government links open unmodified — no UTM, no log.
    if (!item.tracked) return; // let default href open
    e.preventDefault();
    const target = buildTrackedUrl(item.url, item.campaign);
    // Log click (non-blocking)
    logServiceClick({
      service_type: service.slug,
      partner_name: item.name,
      user_id: user?.id,
      user_district: user?.district,
      user_state: user?.state,
    });
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const trackedHref = (item) => (item.tracked ? buildTrackedUrl(item.url, item.campaign) : item.url);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/30 via-white to-white">
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid={`service-detail-${slug}`}>
        {/* Back */}
        <button
          onClick={() => navigate('/money-hub')}
          className="mb-5 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
          data-testid="back-to-services"
        >
          <ArrowLeft className="w-4 h-4" /> Back to KMS Services
        </button>

        {/* Hero */}
        <div className="mb-8 flex items-start gap-4">
          <div className="text-5xl leading-none">{service.icon}</div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
              <span className={`bg-gradient-to-r ${ACCENT_GRAD[service.accent]} bg-clip-text text-transparent`}>{service.title}</span>
            </h1>
            <p className="mt-2 text-base text-gray-600 max-w-2xl leading-relaxed">{service.subtitle}</p>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {service.sections.map((section, idx) => (
            <section key={section.heading} data-testid={`service-section-${section.kind}-${idx}`}>
              <div className="flex items-center gap-2 mb-4">
                {section.kind === 'gov' ? (
                  <Landmark className="w-5 h-5 text-emerald-700" />
                ) : (
                  <Building2 className="w-5 h-5 text-gray-700" />
                )}
                <h2 className="text-lg font-semibold text-gray-900">{section.heading}</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {section.items.map((item) => (
                  <div
                    key={item.campaign}
                    className={`bg-white rounded-2xl border p-5 hover:shadow-md transition ${
                      section.kind === 'gov' || item.govBadge
                        ? 'border-emerald-200'
                        : 'border-gray-200'
                    }`}
                    data-testid={`partner-${service.slug}-${item.campaign}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {(section.kind === 'gov' || item.govBadge) ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                            <ShieldCheck className="w-3 h-3" /> Govt
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded">
                            Partner
                          </span>
                        )}
                      </div>
                    </div>

                    <h3 className="mt-2 text-base font-semibold text-gray-900">{item.name}</h3>

                    <dl className="mt-2 space-y-1 text-sm text-gray-700">
                      {item.amount && (
                        <div><span className="text-gray-500">Amount: </span>{item.amount}</div>
                      )}
                      {item.interest && (
                        <div><span className="text-gray-500">Interest: </span>{item.interest}</div>
                      )}
                      {item.cover && (
                        <div><span className="text-gray-500">Cover: </span>{item.cover}</div>
                      )}
                      {item.cost && (
                        <div><span className="text-gray-500">Cost: </span>{item.cost}</div>
                      )}
                      {item.who && (
                        <div><span className="text-gray-500">Who can apply: </span>{item.who}</div>
                      )}
                      {item.summary && (
                        <div className="leading-relaxed">{item.summary}</div>
                      )}
                    </dl>

                    <a
                      href={trackedHref(item)}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      onClick={(e) => handlePartnerClick(e, item)}
                      data-testid={`partner-cta-${service.slug}-${item.campaign}`}
                      className={`mt-4 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-white ${ACCENT_BTN[service.accent]} px-3.5 py-2 rounded-lg w-full`}
                    >
                      {item.cta} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Note */}
        {service.note && (
          <div className="mt-8 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-2 text-sm text-emerald-900 leading-relaxed" data-testid="service-note">
            <Info className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" />
            <p>{service.note}</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2 text-sm text-amber-900 leading-relaxed" data-testid="service-disclaimer">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <p>{service.disclaimer}</p>
        </div>
      </main>
    </div>
  );
};

export default ServiceDetail;
